import { useState, type KeyboardEvent } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Switch } from "@project/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import { priceToSumOfRupees } from "@project/types/bhojan";

export type BhojanSchemeFormState = Record<string, unknown>;

export type BhojanSchemeFormFieldsProps = {
  editing: BhojanSchemeFormState | null;
  setEditing: (value: BhojanSchemeFormState | null) => void;
};

function patchEditing(
  editing: BhojanSchemeFormState | null,
  setEditing: BhojanSchemeFormFieldsProps["setEditing"],
  updates: BhojanSchemeFormState
) {
  setEditing({ ...(editing ?? {}), ...updates });
}

function getFeatures(editing: BhojanSchemeFormState | null): string[] {
  const value = editing?.features;
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map(item => String(item).trim()).filter(Boolean);
}

export function BhojanSchemeFormFields({
  editing,
  setEditing,
}: BhojanSchemeFormFieldsProps) {
  const [featureDraft, setFeatureDraft] = useState("");
  const features = getFeatures(editing);

  const setPrice = (raw: string) => {
    patchEditing(editing, setEditing, {
      price: raw,
      sum_of_rupees: priceToSumOfRupees(raw),
    });
  };

  const addFeature = () => {
    const next = featureDraft.trim();
    if (!next) {
      return;
    }
    if (features.some(item => item.toLowerCase() === next.toLowerCase())) {
      setFeatureDraft("");
      return;
    }
    patchEditing(editing, setEditing, {
      features: [...features, next],
    });
    setFeatureDraft("");
  };

  const removeFeature = (index: number) => {
    patchEditing(editing, setEditing, {
      features: features.filter((_, i) => i !== index),
    });
  };

  const onFeatureKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addFeature();
    }
  };

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div>
        <Label data-required="true">Badge Text</Label>
        <Input
          value={String(editing?.badge_text ?? "")}
          onChange={e =>
            patchEditing(editing, setEditing, { badge_text: e.target.value })
          }
          placeholder="10 YEARS SPONSORSHIP"
          className="bg-muted/40 rounded-xl border-transparent"
        />
      </div>

      <div>
        <Label data-required="true">Title</Label>
        <Input
          value={String(editing?.title ?? "")}
          onChange={e =>
            patchEditing(editing, setEditing, { title: e.target.value })
          }
          placeholder="Simple Meal Sponsorship"
          className="bg-muted/40 rounded-xl border-transparent"
        />
      </div>

      <div className="sm:col-span-2">
        <Label data-required="true">Technical Name</Label>
        <Input
          value={String(editing?.technical_name ?? "")}
          onChange={e =>
            patchEditing(editing, setEditing, {
              technical_name: e.target.value,
            })
          }
          placeholder="SIMPLE BHOJAN TITHI"
          className="bg-muted/40 rounded-xl border-transparent"
        />
      </div>

      <div>
        <Label data-required="true">Price (₹)</Label>
        <Input
          type="number"
          min={0}
          value={String(editing?.price ?? "")}
          onChange={e => setPrice(e.target.value)}
          placeholder="51000"
          className="bg-muted/40 rounded-xl border-transparent"
        />
      </div>

      <div>
        <Label data-required="true">Sum of Rupees</Label>
        <Input
          value={String(editing?.sum_of_rupees ?? "")}
          readOnly
          placeholder="Auto-filled from price"
          className="bg-muted/40 cursor-default rounded-xl border-transparent opacity-90"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Auto-generated from Price
        </p>
      </div>

      <div className="sm:col-span-2">
        <Label data-required="true">Features</Label>
        <div className="flex gap-2">
          <Input
            value={featureDraft}
            onChange={e => setFeatureDraft(e.target.value)}
            onKeyDown={onFeatureKeyDown}
            placeholder="Add a feature and press Enter"
            className="bg-muted/40 rounded-xl border-transparent"
          />
          <Button
            type="button"
            variant="secondary"
            className="shrink-0 gap-1 rounded-xl"
            onClick={addFeature}
          >
            <Plus className="h-4 w-4" />
            Add
          </Button>
        </div>

        <div className="mt-3 overflow-hidden rounded-xl border border-border bg-card">
          <Table className="[&_td]:border-border/70 border-collapse [&_td]:border [&_th]:border [&_th]:border-border">
            <TableHeader className="bg-muted/60">
              <TableRow className="hover:bg-muted/60">
                <TableHead className="w-14 px-3 py-2 text-center font-semibold text-foreground">
                  #
                </TableHead>
                <TableHead className="px-3 py-2 font-semibold text-foreground">
                  Feature
                </TableHead>
                <TableHead className="w-20 px-3 py-2 text-right font-semibold text-foreground">
                  Action
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {features.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="px-3 py-6 text-center text-sm text-muted-foreground"
                  >
                    No features added yet
                  </TableCell>
                </TableRow>
              ) : (
                features.map((feature, index) => (
                  <TableRow key={`${feature}`}>
                    <TableCell className="px-3 py-2.5 text-center text-sm text-muted-foreground">
                      {index + 1}
                    </TableCell>
                    <TableCell className="px-3 py-2.5 text-sm text-foreground">
                      {feature}
                    </TableCell>
                    <TableCell className="px-3 py-2.5 text-right">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => removeFeature(index)}
                        aria-label={`Remove feature ${index + 1}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="grid gap-3 sm:col-span-2 sm:grid-cols-3">
        <div>
          <Label>Display Order</Label>
          <Input
            type="number"
            min={0}
            value={String(editing?.display_order ?? "")}
            onChange={e =>
              patchEditing(editing, setEditing, {
                display_order: e.target.value,
              })
            }
            placeholder="1"
            className="bg-muted/40 rounded-xl border-transparent"
          />
        </div>

        <div className="bg-muted/40 flex items-center gap-3 rounded-xl px-3 py-2">
          <Switch
            checked={editing?.is_highlighted === true}
            onCheckedChange={v =>
              patchEditing(editing, setEditing, { is_highlighted: v })
            }
          />
          <div>
            <p className="text-sm font-medium">Highlighted</p>
            <p className="text-xs text-muted-foreground">
              {editing?.is_highlighted === true ? "Yes" : "No"}
            </p>
          </div>
        </div>

        <div className="bg-muted/40 flex items-center gap-3 rounded-xl px-3 py-2">
          <Switch
            checked={editing?.is_active === true}
            onCheckedChange={v =>
              patchEditing(editing, setEditing, { is_active: v })
            }
          />
          <div>
            <p className="text-sm font-medium">Active</p>
            <p className="text-xs text-muted-foreground">
              {editing?.is_active === true ? "Active" : "Inactive"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
